import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Play, ArrowLeft, Clock, Calendar, Star, Users, BookmarkPlus, Heart, Bookmark, Eye, CalendarClock, X, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MovieDetailPage = () => {
  const { movieId } = useParams();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [borrowing, setBorrowing] = useState(false);
  const [liking, setLiking] = useState(false);
  const [watchlisting, setWatchlisting] = useState(false);
  const [showPrebookModal, setShowPrebookModal] = useState(false);
  const [prebookDate, setPrebookDate] = useState('');
  const [prebookSubmitting, setPrebookSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [hasReservation, setHasReservation] = useState(false);
  const [reservationId, setReservationId] = useState(null);
  const { fetchBorrowCount, borrowCount } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMovie();
    fetchReservationStatus();
  }, [movieId]);

  const fetchMovie = async () => {
    try {
      const { data } = await axios.get(`${API}/movies/${movieId}`, { withCredentials: true });
      setMovie(data);
    } catch (error) {
      console.error('Error fetching movie:', error);
      toast.error('Failed to load movie details');
    } finally {
      setLoading(false);
    }
  };

  const fetchReservationStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/reservations`, { withCredentials: true });
      const found = (data || []).find(r => r.movie_id === movieId);
      if (found) {
        setHasReservation(true);
        setReservationId(found.reservation_id);
      } else {
        setHasReservation(false);
        setReservationId(null);
      }
    } catch (error) {
      console.error('Failed to fetch reservation status:', error);
    }
  };

  const handleBorrow = async () => {
    setBorrowing(true);
    try {
      await axios.post(`${API}/borrows/${movieId}`, {}, { withCredentials: true });
      toast.success('Movie borrowed successfully! You have 5 days to watch it.');
      fetchMovie();
      fetchBorrowCount();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to borrow movie');
    } finally {
      setBorrowing(false);
    }
  };

  const handleWatch = () => {
    navigate(`/watch/${movieId}`);
  };

  const handleToggleLike = async () => {
    setLiking(true);
    try {
      const { data } = await axios.post(`${API}/likes/${movieId}`, {}, { withCredentials: true });
      setMovie({ ...movie, liked: data.liked, like_count: data.like_count });
      toast.success(data.liked ? 'Added to your likes' : 'Removed from likes');
    } catch (error) {
      toast.error('Failed to update like');
    } finally {
      setLiking(false);
    }
  };

  const handleToggleWatchlist = async () => {
    setWatchlisting(true);
    try {
      const { data } = await axios.post(`${API}/watchlist/${movieId}`, {}, { withCredentials: true });
      setMovie({ ...movie, in_watchlist: data.in_watchlist });
      toast.success(data.in_watchlist ? 'Added to Wish List' : 'Removed from Wish List');
    } catch (error) {
      toast.error('Failed to update wish list');
    } finally {
      setWatchlisting(false);
    }
  };

  const handlePrebook = async () => {
    setPrebookSubmitting(true);
    try {
      const payload = prebookDate ? { scheduled_date: new Date(prebookDate).toISOString() } : {};
      const { data } = await axios.post(`${API}/reservations/${movieId}`, payload, { withCredentials: true });
      toast.success('Pre-booking confirmed');
      setHasReservation(true);
      setReservationId(data.reservation_id);
      setShowPrebookModal(false);
      setPrebookDate('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to pre-book');
    } finally {
      setPrebookSubmitting(false);
    }
  };

  const fetchAvailability = async (dateStr) => {
    setAvailabilityLoading(true);
    try {
      const params = dateStr ? { date: dateStr } : {};
      const { data } = await axios.get(`${API}/movies/${movieId}/availability`, {
        params, withCredentials: true,
      });
      setAvailability(data);
    } catch (err) {
      setAvailability(null);
    } finally {
      setAvailabilityLoading(false);
    }
  };


  const handleCancelReservation = async () => {
    if (!reservationId) return;
    try {
      await axios.delete(`${API}/reservations/${reservationId}`, { withCredentials: true });
      toast.success('Pre-booking cancelled');
      setHasReservation(false);
      setReservationId(null);
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
        return;
      }
      // Fallback for older/insecure contexts
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Link copied to clipboard');
    } catch (err) {
      toast.error('Could not copy link');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <p style={{ color: '#8197A4' }}>Movie not found</p>
      </div>
    );
  }

  const isFull = movie.current_viewers >= movie.max_concurrent_viewers;
  const cannotBorrow = borrowCount.active_count >= 5 && !movie.is_borrowed;
  const viewersPercentage = (movie.current_viewers / movie.max_concurrent_viewers) * 100;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0F171E' }} data-testid="movie-detail-page">
      <div className="relative w-full" style={{ height: '60vh' }}>
        <img
          src={mediaUrl(movie.banner_path, API) || `https://picsum.photos/seed/${movieId}-banner/1920/800`}
          alt={movie.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient"></div>
        <button
          onClick={() => navigate(-1)}
          className="absolute top-8 left-8 flex items-center gap-2 px-4 py-2 rounded-lg glassmorphism"
          style={{ color: '#FFFFFF' }}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Back
        </button>
      </div>

      <div className="px-4 sm:px-8 md:px-12 lg:px-16 py-8">
        <div className="max-w-6xl">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#FFFFFF' }} data-testid="movie-title">
            {movie.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 rounded text-sm font-semibold" style={{ backgroundColor: '#1E3A8A', color: 'white' }}>
              {movie.age_rating}
            </span>
            <div className="flex items-center gap-2" style={{ color: '#DDDDDD' }}>
              <Calendar size={16} />
              <span>{movie.release_year}</span>
            </div>
            <div className="flex items-center gap-2" style={{ color: '#DDDDDD' }}>
              <Clock size={16} />
              <span>{movie.runtime} min</span>
            </div>
            {movie.like_count > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#EF4444' }}>
                <Heart size={16} fill="#EF4444" />
                <span className="font-semibold">{movie.like_count}</span>
              </div>
            )}
          </div>

          {/* Prominent Viewers Card + Borrow Button side by side */}
          <div className="mb-6 flex flex-wrap items-stretch gap-4">
            <div className="flex-1 min-w-[280px] max-w-md p-5 rounded-2xl flex items-center gap-5 flex-wrap" style={{
              backgroundColor: isFull ? 'rgba(240, 193, 75, 0.1)' : 'rgba(59, 130, 246, 0.1)',
              border: `2px solid ${isFull ? '#F0C14B' : '#3B82F6'}`,
              boxShadow: `0 4px 20px ${isFull ? 'rgba(240, 193, 75, 0.15)' : 'rgba(59, 130, 246, 0.15)'}`
            }} data-testid="viewers-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                  backgroundColor: isFull ? '#F0C14B' : '#3B82F6'
                }}>
                  <Eye size={22} color={isFull ? '#0F171E' : 'white'} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#8197A4' }}>
                    {isFull ? 'Currently Full' : 'Now Watching'}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold" style={{ color: isFull ? '#F0C14B' : '#3B82F6' }} data-testid="current-viewers-count">
                      {movie.current_viewers}
                    </span>
                    <span className="text-2xl font-medium" style={{ color: '#8197A4' }}>
                      / {movie.max_concurrent_viewers}
                    </span>
                    <span className="text-sm font-medium ml-1" style={{ color: '#DDDDDD' }}>viewers</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Big Borrow / Watch Button next to viewers card */}
            {!movie.is_restricted && (
              <div className="flex-1 min-w-[240px] max-w-md">
                {movie.is_borrowed ? (
                  <button
                    onClick={handleWatch}
                    className="w-full h-full p-5 rounded-2xl font-bold text-lg transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-3"
                    style={{
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      boxShadow: '0 4px 20px rgba(59, 130, 246, 0.35)'
                    }}
                    data-testid="watch-movie-button-large"
                  >
                    <Play size={28} fill="white" />
                    Watch Now
                  </button>
                ) : cannotBorrow ? (
                  <div className="w-full h-full p-5 rounded-2xl flex items-center justify-center" style={{
                    backgroundColor: 'rgba(240, 193, 75, 0.1)',
                    border: '2px solid #F0C14B'
                  }}>
                    <p style={{ color: '#F0C14B' }} className="font-medium text-sm text-center">
                      Borrow limit reached (5/5).<br/>Return a movie to borrow this.
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={handleBorrow}
                    disabled={borrowing}
                    className="w-full h-full p-5 rounded-2xl font-bold text-lg transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-3"
                    style={{
                      backgroundColor: isFull ? '#F0C14B' : '#1E3A8A',
                      color: isFull ? '#0F171E' : 'white',
                      boxShadow: `0 4px 20px ${isFull ? 'rgba(240, 193, 75, 0.35)' : 'rgba(30, 58, 138, 0.35)'}`
                    }}
                    data-testid="borrow-movie-button-large"
                  >
                    <BookmarkPlus size={28} />
                    {borrowing ? 'Processing...' : isFull ? 'Join Waitlist' : 'Borrow (5 days)'}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {movie.genre?.map((g) => (
                <span
                  key={g}
                  className="px-3 py-1 rounded-full text-sm"
                  style={{ backgroundColor: '#1A2632', color: '#DDDDDD', border: '1px solid #2A3F52' }}
                >
                  {g}
                </span>
              ))}
            </div>
          </div>

          {movie.is_restricted ? (
            <div className="p-6 rounded-lg mb-6" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', border: '1px solid #DC2626' }} data-testid="age-restricted-message">
              <p style={{ color: '#EF4444' }}>This title is unavailable because of your age classification.</p>
            </div>
          ) : (
            <div className="flex gap-3 mb-8 flex-wrap items-center">
              {/* Like button */}
              <button
                onClick={handleToggleLike}
                disabled={liking}
                className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: movie.liked ? 'rgba(239, 68, 68, 0.15)' : '#1A2632',
                  border: `1px solid ${movie.liked ? '#EF4444' : '#2A3F52'}`,
                  color: movie.liked ? '#EF4444' : '#FFFFFF'
                }}
                data-testid="like-button"
                title={movie.liked ? 'Unlike' : 'Like'}
              >
                <Heart size={20} fill={movie.liked ? '#EF4444' : 'transparent'} />
                <span>{movie.liked ? 'Liked' : 'Like'}</span>
              </button>

              {/* Wish List button */}
              <button
                onClick={handleToggleWatchlist}
                disabled={watchlisting}
                className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: movie.in_watchlist ? 'rgba(240, 193, 75, 0.15)' : '#1A2632',
                  border: `1px solid ${movie.in_watchlist ? '#F0C14B' : '#2A3F52'}`,
                  color: movie.in_watchlist ? '#F0C14B' : '#FFFFFF'
                }}
                data-testid="watchlist-button"
                title={movie.in_watchlist ? 'Remove from Wish List' : 'Add to Wish List'}
              >
                <Bookmark size={20} fill={movie.in_watchlist ? '#F0C14B' : 'transparent'} />
                <span>{movie.in_watchlist ? 'On Wish List' : 'Wish List'}</span>
              </button>

              {/* Share button — copies the current page URL */}
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: '#1A2632',
                  border: '1px solid #2A3F52',
                  color: '#FFFFFF'
                }}
                data-testid="share-button"
                title="Copy link to share"
              >
                <Share2 size={20} />
                <span>Share</span>
              </button>

              {/* Pre-book button */}
              {hasReservation ? (
                <button
                  onClick={handleCancelReservation}
                  className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                    border: '1px solid #10B981',
                    color: '#10B981'
                  }}
                  data-testid="cancel-prebook-button"
                  title="Cancel pre-booking"
                >
                  <CalendarClock size={20} />
                  <span>Pre-booked</span>
                  <X size={14} className="ml-1" />
                </button>
              ) : (
                <button
                  onClick={() => { setShowPrebookModal(true); fetchAvailability(''); }}
                  disabled={movie.is_borrowed}
                  className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50"
                  style={{
                    backgroundColor: '#1A2632',
                    border: '1px solid #3B82F6',
                    color: '#3B82F6'
                  }}
                  data-testid="prebook-button"
                  title="Pre-book this movie up to 30 days in advance"
                >
                  <CalendarClock size={20} />
                  <span>Pre-book</span>
                </button>
              )}
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Synopsis</h2>
            <p className="text-lg leading-relaxed" style={{ color: '#DDDDDD' }}>
              {movie.synopsis}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>Details</h3>
              <div className="space-y-2">
                <div>
                  <span className="font-medium" style={{ color: '#8197A4' }}>Director: </span>
                  <span style={{ color: '#DDDDDD' }}>{movie.director}</span>
                </div>
                <div>
                  <span className="font-medium" style={{ color: '#8197A4' }}>Language: </span>
                  <span style={{ color: '#DDDDDD' }}>{movie.language}</span>
                </div>
                <div>
                  <span className="font-medium" style={{ color: '#8197A4' }}>Country: </span>
                  <span style={{ color: '#DDDDDD' }}>{movie.country}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>Cast</h3>
              <div className="flex flex-wrap gap-2">
                {movie.cast?.map((actor) => (
                  <span
                    key={actor}
                    className="px-3 py-2 rounded"
                    style={{ backgroundColor: '#1A2632', color: '#DDDDDD', border: '1px solid #2A3F52' }}
                  >
                    {actor}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-book Modal */}
      {showPrebookModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }} data-testid="prebook-modal">
          <div className="w-full max-w-md p-6 rounded-2xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
            <div className="flex items-center gap-3 mb-4">
              <CalendarClock size={26} style={{ color: '#3B82F6' }} />
              <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Pre-book Movie</h2>
            </div>
            <p className="mb-4 text-sm" style={{ color: '#8197A4' }}>
              Reserve <span className="font-semibold" style={{ color: '#FFFFFF' }}>{movie.title}</span> up to 30 days in advance. You&apos;ll get priority when a viewing slot opens.
            </p>
            <label className="block text-sm font-medium mb-2" style={{ color: '#DDDDDD' }}>Preferred date (optional)</label>
            <input
              type="date"
              value={prebookDate}
              min={new Date().toISOString().slice(0, 10)}
              max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}
              onChange={(e) => { setPrebookDate(e.target.value); fetchAvailability(e.target.value); }}
              className="w-full px-4 py-2 rounded-lg outline-none mb-4 date-input-dark"
              style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF', colorScheme: 'dark' }}
              data-testid="prebook-date-input"
            />

            {/* Availability panel */}
            <div className="mb-6 p-4 rounded-lg" style={{
              backgroundColor: '#0F171E',
              border: `1px solid ${availability?.status === 'full' ? '#EF4444' : availability?.status === 'last_slot' ? '#F0C14B' : availability?.status === 'limited' ? '#F0C14B' : '#10B981'}`,
            }} data-testid="prebook-availability">
              {availabilityLoading ? (
                <div className="text-sm text-center py-2" style={{ color: '#8197A4' }}>Checking availability…</div>
              ) : !availability ? (
                <div className="text-sm text-center py-2" style={{ color: '#8197A4' }}>Pick a date to see slot availability</div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                      {new Date(availability.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div className="text-xs px-2 py-0.5 rounded-full font-bold" style={{
                      backgroundColor:
                        availability.status === 'full' ? 'rgba(239,68,68,0.15)' :
                        availability.status === 'last_slot' ? 'rgba(240,193,75,0.18)' :
                        availability.status === 'limited' ? 'rgba(240,193,75,0.15)' :
                        'rgba(16,185,129,0.15)',
                      color:
                        availability.status === 'full' ? '#EF4444' :
                        availability.status === 'last_slot' ? '#F0C14B' :
                        availability.status === 'limited' ? '#F0C14B' :
                        '#10B981',
                      border: `1px solid ${
                        availability.status === 'full' ? '#EF4444' :
                        availability.status === 'last_slot' ? '#F0C14B' :
                        availability.status === 'limited' ? '#F0C14B' :
                        '#10B981'
                      }`,
                    }}>
                      {availability.status === 'full' ? 'FULLY BOOKED' :
                        availability.status === 'last_slot' ? 'LAST SLOT' :
                        availability.status === 'limited' ? 'LIMITED' :
                        'AVAILABLE'}
                    </div>
                  </div>
                  {/* Slot bar */}
                  <div className="flex gap-1 mb-2">
                    {Array.from({ length: availability.max_viewers }).map((_, i) => (
                      <div key={i} className="flex-1 h-2 rounded-full" style={{
                        backgroundColor: i < availability.booked
                          ? (availability.status === 'full' ? '#EF4444' : '#F0C14B')
                          : '#10B981',
                        opacity: i < availability.booked ? 1 : 0.5,
                      }} />
                    ))}
                  </div>
                  <div className="text-xs" style={{ color: '#DDDDDD' }}>
                    <span style={{ color: '#8197A4' }}>Slots left:</span>{' '}
                    <span className="font-bold" style={{
                      color: availability.slots_left === 0 ? '#EF4444' : availability.slots_left <= 1 ? '#F0C14B' : '#10B981'
                    }}>
                      {availability.slots_left}
                    </span>
                    <span style={{ color: '#8197A4' }}> of {availability.max_viewers}</span>
                    {' · '}
                    <span style={{ color: '#8197A4' }}>{availability.active_borrows} borrowed, {availability.active_reservations} pre-booked</span>
                  </div>
                  {availability.status === 'full' && (
                    <div className="text-xs mt-2" style={{ color: '#EF4444' }}>
                      All slots taken. You&apos;ll be added to the waitlist ({availability.waitlist_size} ahead of you) and notified when a slot opens.
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowPrebookModal(false); setPrebookDate(''); }}
                className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}
                data-testid="prebook-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handlePrebook}
                disabled={prebookSubmitting}
                className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: availability?.status === 'full' ? '#EF4444' : '#1E3A8A', color: 'white' }}
                data-testid="prebook-confirm-button"
              >
                {prebookSubmitting ? 'Reserving...' : (availability?.status === 'full' ? 'Join Waitlist' : 'Confirm Pre-book')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
